import {json, texture, MiniSignal} from "p3";
import common from "../common";
import definitions from "../definitions";
import QuestFactory from "../game/quest/QuestFactory";
import settings from "../settings";
import ScrollBar from "../ui/ScrollBar";
import Swiper from "../ui/Swiper";
import TextFormat from "../ui/TextFormat";
import {iswebfont, locale, sensitiveUpperCase} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";

export default class QuestScene extends MenuScene {
    constructor(questLog) {
        super("quest");

        this._questLog = questLog;

        this._menuHolder = null;
        this._menuBacking = null;
        this._menuContent = null;
        this._menuMask = null;
        this._bottomScroll = null;
        this._scrollbarRope = null;
        this._scrollbar = null;
        this._swiper = null;
        this._continuousTweens = [];

        this.signals.locationJump = new MiniSignal();
    }

    init() {
        super.init(); 
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        //console.log(this._questLog);

        this.interactive = true;

        if (!common.saveData.settings.musicMute) {
            P3.audio.playMusic("music_quests_loop");
        }
        P3.audio.playSound("vo_bert_hahum_00");

        this.createFill();
        this.createTitleBanner(locale('ui_quests_title'));

        this._menuHolder = new PIXI.Container();
        this._menuHolder.x = common.STAGE_WIDTH/2;
        this._menuHolder.y = 150;
        this.addChild(this._menuHolder);

        this._menuBacking = new PIXI.TilingSprite(texture('quest_panel_tile'), 590, 30);
        this._menuBacking.anchor.x = 0.5;
        this._menuBacking.y = 30;
        this._menuHolder.addChild(this._menuBacking);

        let menuContentHolder = new PIXI.Container();
        menuContentHolder.y = this._menuBacking.y + 30;
        this._menuHolder.addChild(menuContentHolder);

        this._menuContent = new PIXI.Container();
        this._menuContent.scrollPos = 0;
        menuContentHolder.addChild(this._menuContent);

        this._menuMask = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._menuMask.tint = 0x000000;
        this._menuMask.anchor.x = 0.5;
        this._menuMask.y = this._menuBacking.y;
        this._menuMask.width = this._menuBacking.width;
        this._menuMask.height = this._menuBacking.height;
        menuContentHolder.mask = this._menuMask;
        this._menuHolder.addChild(this._menuMask);

        let topScroll = new PIXI.Sprite(texture('quest_panel_edge'));
        topScroll.anchor.x = 0.5;
        this._menuHolder.addChild(topScroll);

        this._bottomScroll = new PIXI.Sprite(texture('quest_panel_edge'));
        this._bottomScroll.anchor.set(0.5);
        this._bottomScroll.y = this._menuBacking.y + this._menuBacking.height + 37;
        this._menuHolder.addChild(this._bottomScroll);

        this._scrollbarRope = new PIXI.NineSlicePlane(P3.assets.texture('scroll_rope'), 10, 25, 10, 25);
        this._scrollbarRope.x = (topScroll.width/2) - 55;
        this._scrollbarRope.y = 10;
        this._scrollbarRope.height = this._menuBacking.height + 50;
        this._menuHolder.addChild(this._scrollbarRope);  

        this._closeButton = this.addCloseButton(this._menuHolder.x + (this._menuHolder.width/2), this._menuHolder.y);
        this._closeButton.overSoundName = "sfx_btn_generic_rollover";
        this._closeButton.clickSoundName = "sfx_btn_quests_close";

        this.fillQuestList();

        this.resize();

        this.animateShow();
    }

    destroy() {
        this._closeButton.destroy();

        if(this._scrollbar) {
            this._scrollbar.destroy();
        }
        if(this._swiper) {
            this._swiper.destroy();
        }

        P3.audio.stopMusic();

        for(let i in this._continuousTweens) {
            this._continuousTweens[i].kill();
        }

        super.destroy();
    }

    update() {
        super.update();

        if(this._scrollbar){
            this._scrollbar.update();
            //this._menuContent.y = -(this._menuContent.height-this._menuMask.height+50) * this._scrollbar.value;
            const h = -(this._menuContent.height-this._menuMask.height+50);

            if(this._scrollbar.dragging) {
                this._menuContent.scrollPos = this._scrollbar.value;
                this._menuContent.y = h * this._menuContent.scrollPos;
            }
            else if(this._swiper){
                this._swiper.update();
                this._menuContent.y = (h * this._menuContent.scrollPos) + this._swiper.y;
                this._scrollbar.value = Math.min(1, Math.max(0, this._menuContent.y / h));
                if (!this._swiper.dragging) {
                    this._menuContent.scrollPos = this._scrollbar.value;
                }
                if (this._menuContent.y > 0) {
                    this._menuContent.y = 0;
                    this._menuContent.scrollPos = 0;
                }
                else if (this._menuContent.y < h) {
                    this._menuContent.y = h;
                    this._menuContent.scrollPos = 1;
                }
                else {
                    this._scrollbar.playSound(Math.abs(this._swiper.y) > 5 ? -this._swiper.y : 0);
                }
                if (!this._swiper.dragging) {
                    this._swiper.reset();
                }
            }
        }
    }

    fillQuestList() {

        let all = this._questLog.getActiveQuests();
        const activeQuestsLength = all.length;
        all = all.concat(this._questLog.getCompleteQuests());

        const mainQuests = [];
        const bonusQuests = [];
        const sideQuests = [];
        const activeQuestLengths = [0, 0, 0];
        for (let i = 0; i < all.length; i++) {
            let j;
            if (definitions.MAIN_QUESTS.indexOf(all[i].owner + '|' + all[i].id) > -1) {
                mainQuests.push(all[i]);
                j = 0;
            } else if (definitions.BONUS_QUESTS.indexOf(all[i].owner + '|' + all[i].id) > -1) {
                bonusQuests.push(all[i]);
                j = 1;
            } else {
                sideQuests.push(all[i]);
                j = 2;
            }
            if (i < activeQuestsLength) {
                activeQuestLengths[j]++;
            }
        }

        const allQuests = [mainQuests, bonusQuests, sideQuests];
        const titles = ["ui_quests_main_quests", "ui_quests_bonus_quests", "ui_quests_side_quests"];

        let currentY = 10;

        for (let q = 0; q < allQuests.length; q++) {

            let list = allQuests[q];

            if (list.length == 0) {
                continue;
            }
            let categoryTitle;
            if (!iswebfont()) {
                categoryTitle = new PIXI.BitmapText(locale(titles[q]), {
                    fontName: "Alghera Pro",
                    fontSize: 27,
                    align: "left",
                    tint: 0x000000,
                    maxWidth: 400
                });
            } else {
                categoryTitle = new PIXI.Text(locale(titles[q]), {
                    fontFamily: "FredFredBurger",
                    fontSize: 27,
                    align: "center",
                    fill: 0x0,
                    wordWrap: true,
                    wordWrapWidth: 400
                });
            }
            categoryTitle.anchor.set(0.5, 0)
            categoryTitle.y = currentY + 3;
            this._menuContent.addChild(categoryTitle);

            currentY += categoryTitle.height + 15;

            for (let i = 0; i < list.length; i++) {
                const npcData = json(list[i].owner);

                //console.log(list[i]);
                //console.log(npcData);

                if (QuestFactory.isMinigame(npcData.quests[list[i].id].type) ||
                    QuestFactory.isBossFight(npcData.quests[list[i].id].type)) {
                    continue;
                }

                const item = new PIXI.Container();
                item.y = currentY;
                this._menuContent.addChild(item);

                const complete = i >= activeQuestLengths[q];

                const itemFrame = new PIXI.NineSlicePlane(texture(complete ? 'quest_frame_complete_9slice' : (q == 0 ? "quest_frame_main_9slice 2" : 'quest_bonus_frame_9slice')), 25, 25, 25, 25);
                itemFrame.width = 500;
                itemFrame.x = -itemFrame.width/2;
                item.addChild(itemFrame);

                if (q == 0) {
                    const ribbon = new PIXI.Sprite(texture('quest_ribbon_main'));
                    ribbon.x = itemFrame.x + 12;
                    item.addChild(ribbon);
                }

                const npcIcon = new PIXI.Sprite(texture(npcData.quests[list[i].id].avatar || npcData.character.avatar));
                npcIcon.x = -244;
                npcIcon.y = -10;
                item.addChild(npcIcon);

                const npcMargin = 0;

                let titleLabel;
                if (!iswebfont()) {
                    titleLabel = new PIXI.BitmapText(sensitiveUpperCase(locale(list[i].name)), {
                        fontName: "Alghera Pro",
                        fontSize: 28,
                        align: "left",
                        tint: 0x000000,
                        maxWidth: 230
                    });
                } else {
                    titleLabel = new PIXI.Text(sensitiveUpperCase(locale(list[i].name)), {
                        fontFamily: "FredFredBurger",
                        fontSize: 28,
                        align: "center",
                        fill: 0x0,
                        wordWrap: true,
                        wordWrapWidth: 230
                    });
                }
                titleLabel.x = npcIcon.x + npcIcon.width + npcMargin;
                titleLabel.y = !iswebfont() ? 18 : 12;
                item.addChild(titleLabel);

                let descriptionLabel;
                if (!iswebfont()) {
                    descriptionLabel = new PIXI.BitmapText(TextFormat.formatTextVar(locale(list[i].description), definitions.TEXT_FORMAT_VAR.TARGET, list[i].target), {
                        fontName: "Alghera Pro",
                        fontSize: 26,
                        align: "left",
                        tint: 0x702311,
                        maxWidth: 230
                    });
                } else {
                    descriptionLabel = new PIXI.Text(TextFormat.formatTextVar(locale(list[i].description), definitions.TEXT_FORMAT_VAR.TARGET, list[i].target), {
                        fontFamily: "FredFredBurger",
                        fontSize: 26,
                        align: "center",
                        fill: 0x702311,
                        wordWrap: true,
                        wordWrapWidth: 220
                    });
                }
                descriptionLabel.x = npcIcon.x + npcIcon.width + npcMargin;
                descriptionLabel.y = titleLabel.y + titleLabel.height + (!iswebfont() ? 10 : 2);
                item.addChild(descriptionLabel);

                const margins = {left:6, right:5, top:4, bottom:4};

                const barIcon = new PIXI.Sprite(texture(list[i].icon));
                barIcon.anchor.set(0, 0.5);
               // console.log(list[i].icon, barIcon.width, barIcon.texture);
                barIcon.x = npcIcon.x + npcIcon.width + npcMargin - (barIcon.texture.trim.left);
                
                const barFrame = new PIXI.Sprite(texture('progress_bar_frame'));
                barFrame.x = npcIcon.x + npcIcon.width + npcMargin + barIcon.texture.trim.width - 5;
                barFrame.y = Math.max(110, descriptionLabel.y + descriptionLabel.height + (barIcon.height/2));
                barFrame.scale.set(0.8);
                
                const barFill = new PIXI.Sprite(PIXI.Texture.WHITE);
                barFill.tint = 0x4bb6b5;
                barFill.x = barFrame.x + margins.left;
                barFill.y = barFrame.y + margins.top;
                barFill.width = ((barFrame.width - margins.left - margins.right) * (complete ? 1 : list[i].progress));
                barFill.height = barFrame.height - margins.top - margins.bottom;
                item.addChild(barFill);

                item.addChild(barFrame);

                barIcon.y = barFrame.y + (barFrame.height/2);
                item.addChild(barIcon);

                if (list[i].target > 1) {
                    let progressLabel;
                    if (!iswebfont()) {
                        progressLabel = new PIXI.BitmapText(String(complete ? list[i].target : list[i].value) + '/' + String(list[i].target), {
                            fontName: "Alghera Pro",
                            fontSize: 26,
                            align: "left",
                            tint: 0x702311,
                            maxWidth: 250
                        });
                    } else {
                        progressLabel = new PIXI.Text(String(complete ? list[i].target : list[i].value) + '/' + String(list[i].target), {
                            fontFamily: "FredFredBurger",
                            fontSize: 26,
                            align: "left",
                            fill: 0x702311,
                            wordWrap: true,
                            wordWrapWidth: 250
                        });
                    }
                    progressLabel.anchor.set(0, 0.5);
                    progressLabel.x = barFrame.x + barFrame.width + 5;
                    progressLabel.y = (barFrame.y + (barFrame.height/2)) + (!iswebfont() ? 0 : -4);
                    item.addChild(progressLabel);
                }

                if (npcData.character.location && json(npcData.character.location) && json(npcData.character.location).icon) {

                    const mapIcon = P3.Button.create(
                        texture('btn_quest_location_bg'),
                        texture('btn_quest_location_bg'),
                        texture('btn_quest_location_bg'),
                        texture(json(npcData.character.location).icon)
                    );
                    mapIcon.animateIcon = false;
                    mapIcon.x = 240 - mapIcon.width/2;
                    mapIcon.y = mapIcon.height/2;
                    mapIcon.signals.click.once(() => {
                        P3.audio.playSound("vo_bert_huh_00");
                        this.signals.locationJump.dispatch(npcData.character.location);
                    }, this);
                    item.addChild(mapIcon);

                    if (q == 0 && complete) {
                        mapIcon.visible = false;
                        
                        const mainQuestCompleteIcon = new PIXI.Sprite(texture("icon_main_quest_complete_bg")); 
                        mainQuestCompleteIcon.anchor.set(0.5);
                        mainQuestCompleteIcon.x = 260 - mainQuestCompleteIcon.width/2;
                        mainQuestCompleteIcon.y = mainQuestCompleteIcon.height/2 + 11;
                        item.addChild(mainQuestCompleteIcon);

                        const mainQuestItemIcon = new PIXI.Sprite(texture("prop_ui_" + list[i].item + "_00")); 
                        mainQuestItemIcon.anchor.set(0.5);
                        mainQuestItemIcon.scale.set(0.3);
                        mainQuestItemIcon.y = 6;
                        mainQuestCompleteIcon.addChild(mainQuestItemIcon);
                    }

                    if (!complete && list[i].value == list[i].target && settings.PHASE > 1) {
                        const pointer = new PIXI.Sprite(texture('icon_npc_quest_bubble'));
                        pointer.anchor.set(0.5);
                        pointer.scale.set(0.75);
                        pointer.position.set(20, -20);
                        mapIcon.addChild(pointer);

                        const miniStamp = new PIXI.Sprite(texture('icon_npc_quest_complete'));
                        miniStamp.y = -10;
                        miniStamp.anchor.set(0.5);
                        pointer.addChild(miniStamp);
                        this._continuousTweens.push(
                            gsap.to(miniStamp.scale, 0.3, {x:1.1, y:1.1, yoyo:true, repeat:-1, ease:Sine.easeInOut}),
                            gsap.to(pointer, 0.3, {y:-30, yoyo:true, repeat:-1, ease:Sine.easeInOut})
                        );
                    }
                }

                if (complete) {
                    const stamp = new PIXI.Sprite(texture('icon_quest_complete'));
                    stamp.anchor.set(1, 0.5);
                    stamp.scale.set(0.5);
                    stamp.x = barFrame.x + barFrame.width;
                    stamp.y = barFrame.y + (barFrame.height/2);
                    item.addChild(stamp);
                }

                currentY += item.height + 10;
                itemFrame.height = item.height;
            }
        }
    }

    animateShow(){
        this.animateTitleBannerIn();

        this._closeButton.scale.set(0);
        this._closeButton.interactive = false;

        GameScene.active.addTween(gsap.to(this._menuHolder, 0.4, {delay:0.2, y:this._menuHolder.y, ease:Expo.easeOut, onComplete:() => {
            
            const scrollHeight = 500;
            GameScene.active.addTween(gsap.to(this._menuBacking, 0.7, {height:scrollHeight, ease:Expo.easeOut, onComplete:() => {
                this.animateCloseButtonIn();
                this.animateShowFinished();
            }, onUpdate:() => {
                this._menuMask.height = this._menuBacking.height;
                this._scrollbarRope.height = this._menuBacking.height + 50;
            }}));
            GameScene.active.addTween(gsap.to(this._bottomScroll, 0.7, {y:this._bottomScroll.y + (scrollHeight - this._menuBacking.height), ease:Expo.easeOut}));

        }}));
        this._menuHolder.y = common.STAGE_HEIGHT + 100;
    }

    animateHide(){
        this.animateFillOut();
        this.animateTitleBannerOut();

        this._closeButton.interactive = false;
        GameScene.active.addTween(gsap.to(this._closeButton.scale, 0.3, {x:0, y:0, ease:Sine.easeIn}));

        if(this._scrollbar){
            this._menuHolder.removeChild(this._scrollbar);
            this._scrollbar.destroy();
        }

        GameScene.active.addTween(gsap.to(this._menuBacking, 0.3, {height:30, ease:Expo.easeOut, onComplete:() => {
            GameScene.active.addTween(gsap.to(this._menuHolder, 0.4, {y:common.STAGE_HEIGHT + 100, ease:Sine.easeIn, onComplete:() => {
                this.signals.previous.dispatch();
            }}));
        }, onUpdate:() => {
            this._menuMask.height = this._menuBacking.height;
            this._scrollbarRope.height = this._menuBacking.height + 50;
        }}));
        GameScene.active.addTween(gsap.to(this._bottomScroll, 0.3, {y:this._menuBacking.y + 67, ease:Expo.easeOut}));

    }

    animateShowFinished()
    {
        if(this._menuContent.height < this._menuMask.height)
            return;

        this._scrollbar = new ScrollBar(texture('scroll_slider'), 310);
        this._scrollbar.y = this._scrollbarRope.y + (this._scrollbar.height/2) - 10;
        this._scrollbar.x = this._scrollbarRope.x - (this._scrollbar.width/2) + 14;
        this._menuHolder.addChild(this._scrollbar);
        this._scrollbar.init();

        this._swiper = new Swiper({x:(common.STAGE_WIDTH/2) - (this._menuMask.width/2), y:this._menuMask.worldTransform.ty, width:this._menuMask.width, height:this._menuMask.height});
    }
}

/**
 * @static
 * @type {QuestScene}
 */
QuestScene.active = null;